import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  canonicalGenreOptionsForValues,
  canonicalGenreValue,
} from '../../util/shelfyOptions';
import { REVIEW_TYPE_OF_PROVIDER, propTypes } from '../../util/types';
import {
  Heading,
  H2,
  H4,
  ListingCard,
  OffersList,
  Reviews,
  ButtonTabNavHorizontal,
} from '../../components';

import ShopAboutSection from './ShopAboutSection';
import css from './ShopTabs.module.css';

const TAB_NAMES = {
  LISTINGS: 'listings',
  REVIEWS: 'reviews',
  ABOUT: 'about',
  OFFERS: 'offers',
};
const BOOK_LISTING_TYPE = 'book-listing';
const MERCH_LISTING_TYPE = 'collector-merch';

const listingCategoryValue = listing => {
  const publicData = listing?.attributes?.publicData || {};
  return publicData.genre || publicData.categoryLevel1 || '';
};

export const shopCategoryOptions = (listings = []) => {
  return canonicalGenreOptionsForValues(listings.map(listingCategoryValue));
};

/**
 * ShopTabs component displays the shop content in tabs:
 * - For Sale (listings)
 * - Reviews (seller reviews)
 * - About (shop bio and specialties)
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.listing>} props.listings - Seller's published listings
 * @param {Array<propTypes.review>} props.reviews - Seller/provider reviews
 * @param {propTypes.user} props.seller - The seller user
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.isCurrentUser - Is this the current user's shop
 * @param {PropTypes.bool} props.queryListingsError - Error loading listings
 * @param {PropTypes.bool} props.queryReviewsError - Error loading reviews
 * @returns {JSX.Element}
 */
const ShopTabs = props => {
  const {
    listings = [],
    reviews = [],
    seller,
    currentUser,
    isCurrentUser,
    queryListingsError,
    queryReviewsError,
  } = props;

  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(TAB_NAMES.LISTINGS);
  const [mounted, setMounted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [listingType, setListingType] = useState('all');
  const [condition, setCondition] = useState('all');
  const [author, setAuthor] = useState('all');
  const [genre, setGenre] = useState('all');
  const [edition, setEdition] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const providerReviews = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const bookListings = listings.filter(
    listing => listing.attributes?.publicData?.listingType === BOOK_LISTING_TYPE
  );
  const merchListings = listings.filter(
    listing => listing.attributes?.publicData?.listingType === MERCH_LISTING_TYPE
  );
  const hasListings = listings.length > 0;
  const hasReviews = providerReviews.length > 0;
  const conditions = Array.from(
    new Set(listings.map(listing => listing.attributes?.publicData?.condition).filter(Boolean))
  );
  const valuesFor = keys => Array.from(new Set(listings.map(listing => {
    const data = listing.attributes?.publicData || {};
    return keys.map(key => data[key]).find(Boolean);
  }).filter(Boolean)));
  const authors = valuesFor(['author', 'bookAuthor']);
  const genres = shopCategoryOptions(listings);
  const editions = valuesFor(['editionsource', 'edition', 'retailer']);
  const filteredListings = listings
    .filter(listing => {
      const { title = '', publicData = {} } = listing.attributes || {};
      const searchable = [title, publicData.author, publicData.bookAuthor, publicData.series, publicData.keywords]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return (
        searchable.includes(searchTerm.trim().toLowerCase()) &&
        (listingType === 'all' || publicData.listingType === listingType) &&
        (condition === 'all' || publicData.condition === condition) &&
        (author === 'all' || publicData.author === author || publicData.bookAuthor === author) &&
        (genre === 'all' ||
          canonicalGenreValue(listingCategoryValue(listing)) === genre ||
          listingCategoryValue(listing) === genre) &&
        (edition === 'all' || publicData.editionsource === edition || publicData.edition === edition || publicData.retailer === edition) &&
        (priceRange === 'all' || (priceRange === 'under-25' && (listing.attributes.price?.amount || 0) < 2500) || (priceRange === '25-75' && (listing.attributes.price?.amount || 0) >= 2500 && (listing.attributes.price?.amount || 0) <= 7500) || (priceRange === 'over-75' && (listing.attributes.price?.amount || 0) > 7500))
      );
    })
    .sort((left, right) => {
      if (sortBy === 'price-low') return (left.attributes.price?.amount || 0) - (right.attributes.price?.amount || 0);
      if (sortBy === 'price-high') return (right.attributes.price?.amount || 0) - (left.attributes.price?.amount || 0);
      return new Date(right.attributes.createdAt || 0) - new Date(left.attributes.createdAt || 0);
    });
  const renderListingSection = (titleId, items) =>
    items.length > 0 ? (
      <section className={css.listingSection}>
        <H2 className={css.sectionTitle}>
          <FormattedMessage id={titleId} values={{ count: items.length }} />
        </H2>
        <ul className={css.listingsList}>
          {items.map(listing => (
            <li key={listing.id.uuid} className={css.listingItem}>
              <ListingCard
                listing={listing}
                showAuthorInfo={false}
                to={{
                  state: {
                    from: { pathname: `/shop/${seller.id.uuid}` },
                    backLabel: isCurrentUser
                      ? 'Back to My Shop'
                      : `Back to ${seller.attributes?.profile?.publicData?.shopName || 'Shop'}`,
                  },
                }}
              />
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const tabs = [
    {
      text: intl.formatMessage(
        { id: 'ShopTabs.forSaleTab' },
        { count: listings.length }
      ),
      key: TAB_NAMES.LISTINGS,
      selected: activeTab === TAB_NAMES.LISTINGS,
      onClick: () => setActiveTab(TAB_NAMES.LISTINGS),
    },
    {
      text: intl.formatMessage(
        { id: 'ShopTabs.reviewsTab' },
        { count: providerReviews.length }
      ),
      key: TAB_NAMES.REVIEWS,
      selected: activeTab === TAB_NAMES.REVIEWS,
      onClick: () => setActiveTab(TAB_NAMES.REVIEWS),
    },
    {
      text: intl.formatMessage({ id: 'ShopTabs.aboutTab' }),
      key: TAB_NAMES.ABOUT,
      selected: activeTab === TAB_NAMES.ABOUT,
      onClick: () => setActiveTab(TAB_NAMES.ABOUT),
    },
    ...(isCurrentUser ? [{
      text: 'Offers',
      key: TAB_NAMES.OFFERS,
      selected: activeTab === TAB_NAMES.OFFERS,
      onClick: () => setActiveTab(TAB_NAMES.OFFERS),
    }] : []),
    {
      text: 'Sold',
      key: 'sold',
      selected: activeTab === 'sold',
      onClick: () => setActiveTab('sold'),
    },
  ];

  return (
    <div className={css.root}>
      <nav className={css.tabNav} role="tablist">
        <div className={css.tabsWrapper}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={classNames(css.tab, {
                [css.tabActive]: tab.selected,
              })}
              onClick={tab.onClick}
              role="tab"
              aria-selected={tab.selected}
            >
              {tab.text}
            </button>
          ))}
        </div>
      </nav>

      <div className={css.tabContent}>
        {activeTab === TAB_NAMES.LISTINGS && (
          <div className={css.tabPane}>
            {hasListings ? (
              <div>
                <H2 className={css.tabTitle}>
                  <FormattedMessage
                    id="ShopTabs.forSaleHeading"
                    values={{ count: listings.length }}
                  />
                </H2>
                <div className={css.listingSections}>
                  <div className={css.shopControls}>
                    <div className={css.shopSearchRow}>
                      <input
                        className={css.shopSearch}
                        type="search"
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                        placeholder="Search this shop"
                        aria-label="Search this shop"
                      />
                      <button
                        type="button"
                        className={css.filtersButton}
                        aria-expanded={filtersOpen}
                        aria-controls="shop-listing-filters"
                        onClick={() => setFiltersOpen(open => !open)}
                      >
                        <FormattedMessage id="ShopTabs.filtersButton" />
                      </button>
                    </div>
                    {filtersOpen ? (
                      <div id="shop-listing-filters" className={css.filtersPanel} aria-label="Shop filters">
                        <div className={css.filtersHeader}>
                          <strong><FormattedMessage id="ShopTabs.filtersHeading" /></strong>
                          <button
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                            aria-label={intl.formatMessage({ id: 'ShopTabs.closeFilters' })}
                          >
                            ×
                          </button>
                        </div>
                        <label className={css.filterLabel}>
                          <span>Format</span>
                          <select value={listingType} onChange={event => setListingType(event.target.value)}>
                            <option value="all">All formats</option>
                            <option value={BOOK_LISTING_TYPE}>Books</option>
                            <option value={MERCH_LISTING_TYPE}>Merchandise</option>
                          </select>
                        </label>
                        {conditions.length ? (
                          <label className={css.filterLabel}>
                            <span>Condition</span>
                            <select value={condition} onChange={event => setCondition(event.target.value)}>
                              <option value="all">All conditions</option>
                              {conditions.map(value => <option key={value} value={value}>{value}</option>)}
                            </select>
                          </label>
                        ) : null}
                        {authors.length ? (
                          <label className={css.filterLabel}>
                            <span>Author</span>
                            <select value={author} onChange={event => setAuthor(event.target.value)}>
                              <option value="all">All authors</option>
                              {authors.map(value => <option key={value} value={value}>{value}</option>)}
                            </select>
                          </label>
                        ) : null}
                        {genres.length ? (
                          <label className={css.filterLabel}>
                            <span>Category</span>
                            <select value={genre} onChange={event => setGenre(event.target.value)}>
                              <option value="all">All categories</option>
                              {genres.map(option => <option key={option.option} value={option.option}>{option.label}</option>)}
                            </select>
                          </label>
                        ) : null}
                        {editions.length ? (
                          <label className={css.filterLabel}>
                            <span>Edition or retailer</span>
                            <select value={edition} onChange={event => setEdition(event.target.value)}>
                              <option value="all">All editions</option>
                              {editions.map(value => <option key={value} value={value}>{value}</option>)}
                            </select>
                          </label>
                        ) : null}
                        <label className={css.filterLabel}>
                          <span>Price</span>
                          <select value={priceRange} onChange={event => setPriceRange(event.target.value)}>
                            <option value="all">All prices</option>
                            <option value="under-25">Under $25</option>
                            <option value="25-75">$25 to $75</option>
                            <option value="over-75">Over $75</option>
                          </select>
                        </label>
                        <label className={css.filterLabel}>
                          <span>Sort by</span>
                          <select value={sortBy} onChange={event => setSortBy(event.target.value)}>
                            <option value="newest">Newest</option>
                            <option value="price-low">Price low to high</option>
                            <option value="price-high">Price high to low</option>
                          </select>
                        </label>
                      </div>
                    ) : null}
                  </div>
                  <p className={css.resultCount}>{filteredListings.length} listings found</p>
                  {filteredListings.length ? renderListingSection('ShopTabs.booksHeading', filteredListings.filter(l => l.attributes?.publicData?.listingType === BOOK_LISTING_TYPE)) : null}
                  {filteredListings.length ? renderListingSection('ShopTabs.merchandiseHeading', filteredListings.filter(l => l.attributes?.publicData?.listingType === MERCH_LISTING_TYPE)) : null}
                  {!filteredListings.length ? <div className={css.emptyState}><p>No listings match your search.</p></div> : null}
                </div>
              </div>
            ) : (
              <div className={css.emptyState}>
                <p>
                  {isCurrentUser ? (
                    <FormattedMessage id="ShopTabs.emptyListingsOwner" />
                  ) : (
                    <FormattedMessage id="ShopTabs.emptyListingsPublic" />
                  )}
                </p>
              </div>
            )}
            {queryListingsError ? (
              <p className={css.error}>
                <FormattedMessage id="ShopTabs.loadingListingsError" />
              </p>
            ) : null}
          </div>
        )}

        {activeTab === TAB_NAMES.REVIEWS && (
          <div className={css.tabPane}>
            {hasReviews ? (
              <div>
                <H2 className={css.tabTitle}>
                  <FormattedMessage
                    id="ShopTabs.reviewsHeading"
                    values={{ count: providerReviews.length }}
                  />
                </H2>
                <Reviews reviews={providerReviews} />
              </div>
            ) : (
              <div className={css.emptyState}>
                <p>
                  <FormattedMessage id="ShopTabs.emptyReviews" />
                </p>
              </div>
            )}
            {queryReviewsError ? (
              <p className={css.error}>
                <FormattedMessage id="ShopTabs.loadingReviewsError" />
              </p>
            ) : null}
          </div>
        )}

        {activeTab === 'sold' && (
          <div className={css.emptyState}>
            <p>Sold listing history is not available yet.</p>
          </div>
        )}

        {activeTab === TAB_NAMES.ABOUT && (
          <div className={css.tabPane}>
            <ShopAboutSection
              seller={seller}
              isCurrentUser={isCurrentUser}
              queryReviewsError={queryReviewsError}
            />
          </div>
        )}

        {activeTab === TAB_NAMES.OFFERS && isCurrentUser ? (
          <div className={css.tabPane}>
            <H2 className={css.tabTitle}>Offers</H2>
            <OffersList sellerView />
          </div>
        ) : null}
      </div>
    </div>
  );
};

ShopTabs.propTypes = {
  listings: PropTypes.arrayOf(propTypes.listing),
  reviews: PropTypes.arrayOf(propTypes.review),
  seller: propTypes.user.isRequired,
  currentUser: propTypes.currentUser,
  isCurrentUser: PropTypes.bool,
  queryListingsError: PropTypes.object,
  queryReviewsError: PropTypes.object,
};

ShopTabs.defaultProps = {
  listings: [],
  reviews: [],
  isCurrentUser: false,
};

export default ShopTabs;
